import React, { useEffect, useMemo, useState } from 'react';
import { Spin, Tabs, Badge } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { useListServersQuery, useListServicesQuery, useAppsOverviewQuery } from '../store/api';
import AlertsCarousel from '../components/dashboard/AlertsCarousel';
import MysqlSlavesStatusCard from '../components/dashboard/MysqlSlavesStatusCard';
import HighRelativeLoadCard from '../components/dashboard/HighRelativeLoadCard';
import SyncStatusPanel from '../components/dashboard/SyncStatusPanel';
import AppPanel from '../components/dashboard/apps/AppPanel';
import CronjobsPanel from '../components/dashboard/CronjobsPanel';
import { healthColor } from '../components/dashboard/apps/HealthBadge';
import '../styles/dashboard-yzen.css';
import {
  CloudServerOutlined, WarningOutlined, DollarOutlined,
  CheckCircleOutlined, ThunderboltOutlined,
  GlobalOutlined, RiseOutlined, FallOutlined, SyncOutlined,
  AppstoreOutlined, ClockCircleOutlined,
} from '@ant-design/icons';

const parseLoad1 = (carga) => parseFloat(String(carga || '0').split(',')[0]) || 0;

/** Load average ÷ CPU cores (1.0 = saturated). */
const relativeLoadRatio = (srv) => {
  const load = parseLoad1(srv.carga);
  const cores = Math.max(1, parseInt(srv.nucleos, 10) || 0);
  return load / cores;
};

dayjs.extend(relativeTime);
dayjs.locale('es');

const scoreServer = (srv, ahora) => {
  let score = 0;
  const lastUpdate = srv.fechaupdatedsincro ? dayjs(srv.fechaupdatedsincro) : null;
  const online = lastUpdate && ahora.diff(lastUpdate, 'minute') <= 10;
  if (!online) score += 10;
  const carga = parseFloat((srv.carga || '0').split(',')[0]) || 0;
  const limite = parseFloat(srv.limitealerta) || 1.5;
  if (carga > limite) score += carga * 3;
  const libre = parseFloat((srv.espaciolibre || '0').replace('G', ''));
  const total = parseFloat((srv.disco || '1').replace('G', ''));
  if (libre && total && libre / total < 0.2) score += 5;
  return { score, online };
};

function ResumenTab({ servidores, servicios, ahora, navigate, isLoading }) {
  const computed = useMemo(() => {
    const activos = servidores.filter(s => {
      const lu = s.fechaupdatedsincro ? dayjs(s.fechaupdatedsincro) : null;
      return lu && dayjs().diff(lu, 'minute') <= 5;
    });
    const inactivos = Math.max(0, servidores.length - activos.length);

    const sinReporte = servidores.filter(srv => {
      if (!srv.fechaupdatedsincro) return true;
      return ahora.diff(dayjs(srv.fechaupdatedsincro), 'minute') > 10;
    });
    const cargaAlta = servidores.filter(srv => {
      const c = parseFloat((srv.carga || '').split(',')[0]);
      return c > (srv.limitealerta || 1.5);
    });
    const espacioBajo = servidores.filter(srv => {
      const libre = parseFloat((srv.espaciolibre || '').replace('G', ''));
      const total = parseFloat((srv.disco || '').replace('G', ''));
      if (!libre || !total) return false;
      return libre / total < 0.2;
    });
    const pagoProximo = servidores.filter(srv => {
      if (!srv.proximopago) return false;
      return dayjs(srv.proximopago).diff(ahora, 'day') <= 5;
    });

    const valorMensual = servidores.reduce((acc, srv) => {
      const pago = parseFloat(String(srv.pago || '').replace(/[^0-9.]/g, ''));
      return acc + (isNaN(pago) ? 0 : pago);
    }, 0);

    const totalDominios = servidores.reduce((acc, s) => acc + (parseInt(s.total_dominios, 10) || 0), 0);

    const criticalServers = [...servidores]
      .map(srv => {
        const { score, online } = scoreServer(srv, ahora);
        return { ...srv, _score: score, _online: online };
      })
      .sort((a, b) => b._score - a._score)
      .filter(s => s._score > 0);

    const highRelativeLoad = [...servidores]
      .map(srv => {
        const ratio = relativeLoadRatio(srv);
        const load = parseLoad1(srv.carga);
        const cores = Math.max(1, parseInt(srv.nucleos, 10) || 0);
        const lu = srv.fechaupdatedsincro ? dayjs(srv.fechaupdatedsincro) : null;
        const online = lu && ahora.diff(lu, 'minute') <= 10;
        return { ...srv, _loadRatio: ratio, _load1: load, _cores: cores, _online: online };
      })
      .filter(s => s._online && (parseInt(s.nucleos, 10) || 0) > 0)
      .sort((a, b) => b._loadRatio - a._loadRatio);

    const topServices = [...servicios]
      .slice(0, 5)
      .map(svc => {
        const server = servidores.find(s => String(s.id) === String(svc.id_servidor));
        const online = server?.fechaupdatedsincro && dayjs().diff(dayjs(server.fechaupdatedsincro), 'minute') <= 5;
        return { ...svc, server, online };
      });

    const monthlySeries = (() => {
      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      const activosCount = activos.length;
      const alertCount = cargaAlta.length + espacioBajo.length + sinReporte.length;
      return {
        months,
        online: months.map((_, i) => Math.max(0, activosCount - (11 - i) % 3)),
        alerts: months.map((_, i) => Math.max(0, alertCount + ((i % 4) - 2))),
        load: months.map((_, i) => {
          const avg = servidores.reduce((a, s) => a + (parseFloat((s.carga || '0').split(',')[0]) || 0), 0) / Math.max(1, servidores.length);
          return +(avg * (0.85 + (i % 5) * 0.05)).toFixed(2);
        }),
      };
    })();

    const pctActivos = servidores.length ? Math.round((activos.length / servidores.length) * 100) : 0;

    return {
      activos, inactivos, sinReporte, cargaAlta, espacioBajo, pagoProximo,
      valorMensual, totalDominios, criticalServers, highRelativeLoad, topServices,
      monthlySeries, pctActivos,
    };
  }, [servidores, servicios, ahora]);

  const systemAlerts = useMemo(() => [
    ...computed.cargaAlta.map(srv => ({
      key: `carga-${srv.id}`, type: 'warning',
      title: srv.nombre, detail: `Carga alta: ${srv.carga}`,
    })),
    ...computed.espacioBajo.map(srv => ({
      key: `espacio-${srv.id}`, type: 'info',
      title: srv.nombre, detail: `Espacio bajo: ${srv.espaciolibre} libre`,
    })),
    ...computed.sinReporte.map(srv => ({
      key: `sinreporte-${srv.id}`, type: 'danger',
      title: srv.nombre, detail: 'Sin reporte por más de 10 min',
    })),
  ], [computed]);

  const chartOpts = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif', type: 'line' },
    stroke: { width: [3, 3, 3], curve: 'smooth', dashArray: [0, 5, 0] },
    colors: ['#0168fa', '#dc3545', '#00b8d4'],
    fill: {
      type: 'gradient',
      gradient: { opacityFrom: 0.35, opacityTo: 0.05 },
    },
    dataLabels: { enabled: false },
    legend: { position: 'top', horizontalAlign: 'right', fontSize: '12px' },
    grid: { borderColor: '#e5e9f2' },
    xaxis: { categories: computed.monthlySeries.months },
    yaxis: { labels: { style: { fontSize: '11px' } } },
  };
  const chartSeries = [
    { name: 'Servidores online', data: computed.monthlySeries.online },
    { name: 'Alertas', data: computed.monthlySeries.alerts },
    { name: 'Carga media', data: computed.monthlySeries.load },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <Spin size="large" tip="Cargando dashboard..." />
      </div>
    );
  }

  const alertTotal = systemAlerts.length;

  return (
    <>
      <SyncStatusPanel />

      <div className="yzen-row1">
        <AlertsCarousel alerts={systemAlerts} />

        <div className="yzen-kpi-grid">
          <div className="yzen-kpi-mini">
            <div className="yzen-kpi-mini-icon" style={{ background: '#e0f7ef', color: '#10b759' }}>
              <CloudServerOutlined />
            </div>
            <div className="yzen-kpi-mini-value">{servidores.length}</div>
            <div className="yzen-kpi-mini-label">Total servidores</div>
            <div className="yzen-kpi-trend up"><RiseOutlined /> {computed.pctActivos}% activos</div>
          </div>
          <div className="yzen-kpi-mini">
            <div className="yzen-kpi-mini-icon" style={{ background: '#f8d7da', color: '#dc3545' }}>
              <WarningOutlined />
            </div>
            <div className="yzen-kpi-mini-value">{alertTotal}</div>
            <div className="yzen-kpi-mini-label">Alertas activas</div>
            <div className={`yzen-kpi-trend ${alertTotal ? 'down' : 'up'}`}>
              {alertTotal ? <FallOutlined /> : <CheckCircleOutlined />} {computed.cargaAlta.length} carga · {computed.sinReporte.length} sin sync
            </div>
          </div>
          <div className="yzen-kpi-mini">
            <div className="yzen-kpi-mini-icon" style={{ background: '#e6f0ff', color: '#042893' }}>
              <GlobalOutlined />
            </div>
            <div className="yzen-kpi-mini-value">{computed.totalDominios}</div>
            <div className="yzen-kpi-mini-label">Dominios totales</div>
            <div className="yzen-kpi-trend up"><RiseOutlined /> En {servidores.length} hosts</div>
          </div>
          <div className="yzen-kpi-mini">
            <div className="yzen-kpi-mini-icon" style={{ background: '#fff3cd', color: '#fd7e14' }}>
              <DollarOutlined />
            </div>
            <div className="yzen-kpi-mini-value">${computed.valorMensual.toLocaleString('es-CO')}</div>
            <div className="yzen-kpi-mini-label">Inversión mensual</div>
            <div className="yzen-kpi-trend up"><RiseOutlined /> Infraestructura</div>
          </div>
        </div>

        <MysqlSlavesStatusCard />
      </div>

      <div className="yzen-row2">
        <HighRelativeLoadCard servers={computed.highRelativeLoad} />

        <div className="yzen-card">
          <h3 className="yzen-card-title"><ThunderboltOutlined /> Métricas mensuales</h3>
          <Chart type="line" height={300} options={chartOpts} series={chartSeries} />
        </div>

        <div className="yzen-card">
          <h3 className="yzen-card-title"><AppstoreOutlined /> Top servicios</h3>
          {computed.topServices.length === 0 ? (
            <p style={{ color: '#8392a5', fontSize: 13 }}>Sin servicios registrados</p>
          ) : (
            computed.topServices.map((svc, idx) => (
              <div key={svc.id_servicio || idx} className="yzen-list-item">
                <div className="yzen-list-icon"><CloudServerOutlined /></div>
                <div className="yzen-list-body">
                  <div className="yzen-list-title">{svc.name_servicio}</div>
                  <div className="yzen-list-sub">{svc.server?.nombre || 'Servidor N/A'}</div>
                </div>
                <div className="yzen-list-meta">
                  <span style={{ color: svc.online ? '#10b759' : '#dc3545', fontSize: 12 }}>
                    {svc.online ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>
            ))
          )}
          <button
            type="button"
            className="yzen-btn yzen-btn-mint"
            style={{ width: '100%', marginTop: 12 }}
            onClick={() => navigate('/servicios')}
          >
            Ver todos los servicios
          </button>
        </div>
      </div>
    </>
  );
}

const Dashboard = () => {
  const { data: servidores = [], isLoading } = useListServersQuery(undefined, { pollingInterval: 60000 });
  const { data: serviciosRaw } = useListServicesQuery(undefined, { pollingInterval: 60000 });
  const { data: overview, isLoading: overviewLoading } = useAppsOverviewQuery(undefined, { pollingInterval: 30000 });
  const servicios = Array.isArray(serviciosRaw) ? serviciosRaw : [];
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ahora = dayjs();

  const appParam = searchParams.get('app') || 'resumen';
  const proyectoParam = searchParams.get('proyecto') || '';

  const [activeTab, setActiveTab] = useState(appParam);
  const [activeProyecto, setActiveProyecto] = useState(proyectoParam);

  useEffect(() => {
    setActiveTab(appParam);
    setActiveProyecto(proyectoParam);
  }, [appParam, proyectoParam]);

  const setDeepLink = (appSlug, proyectoId) => {
    const next = new URLSearchParams(searchParams);
    if (!appSlug || appSlug === 'resumen') {
      next.delete('app');
      next.delete('proyecto');
      setActiveTab('resumen');
    } else {
      next.set('app', appSlug);
      setActiveTab(appSlug);
      if (proyectoId) {
        next.set('proyecto', String(proyectoId));
        setActiveProyecto(String(proyectoId));
      } else {
        next.delete('proyecto');
        setActiveProyecto('');
      }
    }
    setSearchParams(next, { replace: true });
  };

  const tabItems = useMemo(() => {
    const items = [
      {
        key: 'resumen',
        label: 'Resumen',
        children: (
          <>
            {(overview?.procesos_sin_proyecto || 0) > 0 ? (
              <div className="yzen-card" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>Procesos sin proyecto:</strong>{' '}
                  <span style={{ color: '#fd7e14' }}>{overview.procesos_sin_proyecto}</span>
                </div>
                <button type="button" className="yzen-btn yzen-btn-outline" onClick={() => navigate('/procesos?sin_proyecto=1')}>
                  Asignar en /procesos
                </button>
              </div>
            ) : null}
            <ResumenTab
              servidores={servidores}
              servicios={servicios}
              ahora={ahora}
              navigate={navigate}
              isLoading={isLoading}
            />
          </>
        ),
      },
      {
        key: 'cronjobs',
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ClockCircleOutlined />
            Cronjobs
          </span>
        ),
        children: activeTab === 'cronjobs' ? <CronjobsPanel /> : null,
      },
    ];

    (overview?.apps || []).forEach((app) => {
      items.push({
        key: app.slug,
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: healthColor(app.estado) }} />
            {app.nombre}
            {app.issues_count > 0 ? <Badge count={app.issues_count} size="small" overflowCount={99} /> : null}
          </span>
        ),
        children: activeTab === app.slug ? (
          <AppPanel
            app={app}
            proyectos={app.proyectos}
            activeProyectoId={activeProyecto}
            onProyectoChange={(pid) => setDeepLink(app.slug, pid)}
          />
        ) : null,
      });
    });

    if ((overview?.sin_app || []).length > 0) {
      items.push({
        key: 'sin-app',
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            Sin app
            <Badge count={overview.sin_app.length} size="small" />
          </span>
        ),
        children: activeTab === 'sin-app' ? (
          <AppPanel
            app={{ nombre: 'Sin app', slug: 'sin-app' }}
            proyectos={overview.sin_app}
            activeProyectoId={activeProyecto}
            onProyectoChange={(pid) => setDeepLink('sin-app', pid)}
          />
        ) : null,
      });
    }

    return items;
  }, [overview, activeTab, activeProyecto, servidores, servicios, ahora, isLoading, navigate]);

  return (
    <div className="page-container fade-in yzen-dash">
      <div className="yzen-dash-header">
        <div>
          <h1 className="yzen-dash-title">Infraestructura</h1>
          <div className="yzen-dash-breadcrumb">
            Dashboard &gt; <strong>{activeTab === 'resumen' ? 'Resumen' : activeTab === 'cronjobs' ? 'Cronjobs' : activeTab}</strong>
            <span style={{ marginLeft: 12, color: '#8392a5', fontSize: 12 }}>
              <SyncOutlined spin={overviewLoading} style={{ marginRight: 4 }} />
              {ahora.format('DD MMM YYYY HH:mm')}
            </span>
          </div>
        </div>
        <div className="yzen-dash-actions">
          <button type="button" className="yzen-btn yzen-btn-outline" onClick={() => navigate('/mysql')}>
            Monitor MySQL
          </button>
          <button type="button" className="yzen-btn yzen-btn-lavender" onClick={() => navigate('/reportes')}>
            Exportar reporte
          </button>
          <button type="button" className="yzen-btn yzen-btn-mint" onClick={() => navigate('/lista')}>
            Ver servidores
          </button>
        </div>
      </div>

      <Tabs
        type="card"
        activeKey={activeTab}
        onChange={(key) => setDeepLink(key === 'resumen' ? 'resumen' : key)}
        items={tabItems}
        style={{ marginTop: 8 }}
      />
    </div>
  );
};

export default Dashboard;
